import { FC } from 'react';
import {
    Box,
    Button,
    Paper,
    IconButton,
    Typography,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { UseFieldArrayAppend, UseFieldArrayRemove, FieldArrayWithId } from 'react-hook-form';
import { FormTextField, FormFileUpload } from '../../../common/Form';
import { processImageFile } from '../util/image/ImageUtil';
import { defaultOfferPosition } from '../schemas';

interface OfferBuilderMUIProps {
    fields: FieldArrayWithId<any, 'offerPositions', 'id'>[];
    append: UseFieldArrayAppend<any, 'offerPositions'>;
    remove: UseFieldArrayRemove;
}

const OfferBuilderMUI: FC<OfferBuilderMUIProps> = ({ fields, append, remove }) => {
    // Wrap processImageFile to ensure it returns string (never null)
    const processImageWrapper = async (file: File): Promise<string> => {
        const result = await processImageFile(file);
        if (!result) {
            throw new Error('Failed to process image');
        }
        return result;
    };

    const addOfferPosition = () => {
        append(defaultOfferPosition);
    };

    return (
        <Box>
            {fields.map((field, index) => (
                <Paper
                    key={field.id}
                    elevation={2}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 2,
                        position: 'relative',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Position {index + 1}
                        </Typography>
                        {fields.length > 1 && (
                            <IconButton
                                color="error"
                                onClick={() => remove(index)}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                        }}
                    >
                        <FormTextField
                            name={`offerPositions.${index}.productName`}
                            label="Product Name"
                            placeholder="Enter product name"
                            required
                        />
                        <FormTextField
                            name={`offerPositions.${index}.brand`}
                            label="Brand"
                            placeholder="Enter brand"
                        />
                        <FormTextField
                            name={`offerPositions.${index}.description`}
                            label="Description"
                            placeholder="Enter description"
                        />
                        <FormTextField
                            name={`offerPositions.${index}.price`}
                            label="Price"
                            placeholder="0.00"
                            type="number"
                            required
                        />
                        <FormTextField
                            name={`offerPositions.${index}.quantity`}
                            label="Quantity"
                            placeholder="1"
                            type="number"
                            required
                        />
                        <FormTextField
                            name={`offerPositions.${index}.productCode`}
                            label="Product Code"
                            placeholder="Enter product code"
                        />
                        <FormTextField
                            name={`offerPositions.${index}.supplier`}
                            label="Supplier"
                            placeholder="Enter supplier"
                        />

                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <FormTextField
                                name={`offerPositions.${index}.notes`}
                                label="Notes"
                                placeholder="Enter any additional notes"
                                multiline
                                rows={3}
                            />
                        </Box>

                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <FormFileUpload
                                name={`offerPositions.${index}.images`}
                                label="Product Images"
                                helperText="Upload product images (JPEG, PNG, GIF, SVG, WEBP)"
                                accept={{
                                    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.svg', '.webp']
                                }}
                                maxSize={5242880} // 5MB
                                maxFiles={1}
                                multiple={false}
                                showPreview={false}
                                convertToBase64={true}
                                processImage={processImageWrapper}
                            />
                        </Box>
                    </Box>
                </Paper>
            ))}

            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addOfferPosition}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                }}
            >
                Add Position
            </Button>
        </Box>
    );
};

export default OfferBuilderMUI;
